import React, { useRef, useState } from 'react';
import { Box, Typography, Button, Card, CardMedia, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check, ChevronDown, ChevronRight, ChevronLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // Importamos el hook de traducción
import agtfitProService from '../AGTFit/service/agtfitProService'; // Importación del servicio
import AppleSpinner from '../../components/common/AppleSpinner'; // Importación del spinner personalizado

const ACCENT_COLOR = '#007BFE';
const HOVER_COLOR = '#0069d9';
const HEALTH_GREEN = '#4CAF50';
const NOT_AVAILABLE_COLOR = '#9e9e9e'; // Color gris para botones no disponibles

// Nuevo contenedor para el carrusel de categorías principales - asegurando overflow visible en todas las pantallas
const MainCategoriesContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(10),
  width: '100%',
  overflow: 'visible', // Importante: mantener visible en todas las pantallas
  paddingBottom: theme.spacing(2), // Espacio adicional para el efecto de desbordamiento

  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(8),
  },
}));

// Contenedor para las tarjetas con scroll horizontal - manteniendo el desborde y alineación original
const CategoryCardsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: theme.spacing(5),
  paddingRight: theme.spacing(4),
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  // Añadir padding adicional para dar espacio al efecto de hover
  paddingTop: theme.spacing(1),    // Añadir padding superior
  paddingBottom: theme.spacing(3), // Aumentar el padding inferior para el efecto de hover
  // Padding lateral para permitir que el efecto de hover sea visible en los bordes
  paddingLeft: theme.spacing(0.5),
  marginLeft: theme.spacing(-0.5),

  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(3),
    paddingRight: theme.spacing(2),
    // Mantener espacio para el efecto de hover en móvil
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
}));

// Botones de navegación para el carrusel
const NavigationButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  bottom: theme.spacing(-6),
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 10, // Aumentar z-index para asegurar que esté por encima de las tarjetas
  [theme.breakpoints.up('sm')]: {
    right: theme.spacing(3),
  },
  [theme.breakpoints.up('lg')]: {
    right: theme.spacing(4),
  },
}));

// Botones de navegación con los colores específicos
const NavButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#e2e2e7'),
    },

    [theme.breakpoints.down('sm')]: {
      width: '28px',
      height: '28px',
    },
  };
});

// Modificar el CategoryCard para que sea adecuado para el carrusel - manteniendo efecto de hover en todas las pantallas
const CategoryCard = styled(Card)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '20px',
    overflow: 'hidden',
    width: '400px',
    height: '500px',
    flexShrink: 0,
    scrollSnapAlign: 'start',
    boxShadow: 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : 'inherit',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    // Efecto de hover consistente en todas las pantallas
    '&:hover': {
      boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 12px rgba(0,0,0,0.2)'
        : '0 4px 12px rgba(0,0,0,0.08)',
      transform: 'translateY(-2px) scale(1.01)', // Efecto sutilmente más notorio
      zIndex: 5, // Asegurar que la tarjeta con hover esté por encima
      backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.5)' : 'inherit',
    },

    // Responsive adjustments only for smaller screens
    [theme.breakpoints.down('md')]: {
      width: '340px',
      height: '450px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '280px',
      height: '400px',
      // Mantener el mismo efecto de hover en móvil
      '&:hover': {
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 12px rgba(0,0,0,0.2)'
          : '0 4px 12px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px) scale(1.01)',
        zIndex: 5,
        backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.5)' : 'inherit',
      },
    },
  };
});

const CategoryImage = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  width: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
}));

// Overlay para el título y precio sobre la imagen
const CategoryOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(3),
  background: 'linear-gradient(to bottom, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 100%)',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '28px',
  color: '#000000',
  textShadow: '0 1px 2px rgba(255,255,255,0.5)',
  display: 'flex',
  alignItems: 'center',

  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '22px',
  },
}));

const HealthPlusSign = styled('span')({
  color: HEALTH_GREEN,
  fontSize: '18px',
  fontWeight: 700,
  marginLeft: '2px',
  position: 'relative',
  top: '-8px',
});

const CategoryPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '14px',
  color: '#000000',
  opacity: 0.9,
  marginTop: '4px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

const UltraIncludedText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #69BFF9 0%, #B96AF3 35%, #E9685E 58%, #F2AC3E 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  fontWeight: 600,
  fontSize: '17px',
  marginTop: '2px',
  marginBottom: '2px',
  display: 'inline-block',

  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
  },
}));

// Texto específico para AGT Health con color sólido
const HealthText = styled(Typography)(({ theme }) => ({
  color: HEALTH_GREEN, // Verde sólido para Health
  fontWeight: 600,
  fontSize: '17px',
  marginTop: '2px',
  marginBottom: '2px',
  display: 'inline-block',

  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
  },
}));

// Texto específico para AGT Job con color cercano a negro
const JobText = styled(Typography)(({ theme }) => ({
  color: '#333333', // Color sólido cercano a negro para Job
  fontWeight: 600,
  fontSize: '17px',
  marginTop: '2px',
  marginBottom: '2px',
  display: 'inline-block',

  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
  },
}));

const LegalText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #2C5282 0%, #4A5568 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  fontWeight: 600,
  fontSize: '17px',
  marginTop: '2px',
  marginBottom: '2px',
  display: 'inline-block',
 
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
  },
}));

const TranslateText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #3498DB 0%, #9B59B6 100%)', // Azul a púrpura, simbolizando diversidad global
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  fontWeight: 600,
  fontSize: '17px',
  marginTop: '2px',
  marginBottom: '2px',
  display: 'inline-block',
 
  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
  },
}));

// Solo para los botones - ahora más compacto
const CategoryContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '70px',
  backdropFilter: 'blur(1px)',
  backgroundColor: 'transparent',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    height: '60px',
    padding: theme.spacing(1),
  },
}));

const CategoryButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
  width: '100%',
}));

// Botón de Learn More estilizado como enlace
const LearnMoreButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  minWidth: '100px',
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: 'transparent',
  color: '#000000',
  border: 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
  },
  '& .MuiSvgIcon-root, & svg': {
    color: '#000000',
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    minWidth: '90px',
    fontSize: '0.8rem',
  },
}));

// Botón de compra más destacado
const BuyButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  minWidth: '100px',
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: ACCENT_COLOR,
  color: '#ffffff',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: HOVER_COLOR,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    minWidth: '90px',
    fontSize: '0.8rem',
  },
}));

// Botón para servicios no disponibles
const NotAvailableButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  minWidth: '120px', // Un poco más ancho para acomodar el texto más largo
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: NOT_AVAILABLE_COLOR,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#757575', // Un tono más oscuro del gris
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1),
    minWidth: '110px',
    fontSize: '0.7rem', // Un poco más pequeño para que quepa el texto
  },
}));

const OwnedButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  minWidth: '100px',
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: theme.palette.success.main,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    minWidth: '90px',
    fontSize: '0.8rem',
  },
}));

// Botón de acceso para AGT Health
const AccessButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(0.5, 2),
  minWidth: '100px',
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: HEALTH_GREEN,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#3d8b40', // Un tono más oscuro del verde
  },
  display: 'flex',
  alignItems: 'center',
  gap: '4px',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.5),
    minWidth: '90px',
    fontSize: '0.8rem',
  },
}));

const FloatingPrice = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  bottom: '58px',
  left: 0,
  right: 0,
  textAlign: 'center',
  fontWeight: 500,
  fontSize: '14px',
  color: '#000000',
  padding: theme.spacing(1),
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    bottom: '50px',
    fontSize: '13px',
  },
}));

const AGTMainCategoriesSection = ({ 
  mainCategories,
  isPro,
  hasAGTUps,
  hasAGTJob,
  hasAGTFit,
  hasAGTHealth,
  hasAGTLegal,
  hasAGTTranslate,
  handleProCheckout,
  onOpenAGTEdu,
  onOpenAGTUps,
  onOpenAGTJob,
  onOpenAGTFit,
  onOpenAGTHealth,
  onOpenAGTLegal,
  onOpenAGTTranslate,
  scrollToSection,
  isMobile,
  isTablet
}) => {
  const categoriesRef = useRef(null);
  const { t } = useTranslation(); // Hook para traducciones
  const [loadingFit, setLoadingFit] = useState(false); // Estado para el indicador de carga de AGT Fit

  // Función para manejar el scroll horizontal del carrusel de categorías
  const handleCategoryScroll = (direction) => {
    if (categoriesRef.current) {
      // Ajustar el ancho de la tarjeta según el tamaño de la pantalla
      let cardWidth = 400; // Desktop default
      if (isTablet) cardWidth = 340;
      if (isMobile) cardWidth = 280;
      
      const gap = isMobile ? 24 : 40; // Aproximación del gap entre tarjetas (reducido en móvil)
      const scrollAmount = cardWidth + gap;
      categoriesRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Función para manejar la redirección a Stripe para AGT Fit
  const handleAGTFitCheckout = async () => {
    try {
      setLoadingFit(true); // Activar indicador de carga
      const { url } = await agtfitProService.createCheckoutSession();
      window.location.href = url;
    } catch (error) {
      console.error('Error iniciando proceso de pago de AGT Fit:', error);
      setLoadingFit(false); // Desactivar indicador de carga en caso de error
    }
  };

  // Reordenar las categorías según el orden especificado
  const orderedCategories = [...mainCategories];
  
  // Definimos el orden deseado
  const desiredOrder = ["AGT Full", "AGT Pro", "AGT Ups", "AGT Fit", "AGT Health"];
  
  // Ordenamos las categorías según el orden deseado
  orderedCategories.sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.title);
    const indexB = desiredOrder.indexOf(b.title);
    
    // Si ambos están en la lista de orden deseado, respetamos ese orden
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // Si solo uno está en la lista, ese va primero
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    // Si ninguno está en la lista, mantener el orden original
    return 0;
  });

  return (
    <MainCategoriesContainer>
      <CategoryCardsContainer ref={categoriesRef}>
        {orderedCategories.map((category, index) => {
          // Determinar si el usuario ya tiene este producto
          const isOwned = 
            (category.title === "AGT Pro" && isPro) || 
            (category.title === "AGT Ups" && hasAGTUps) ||
            (category.title === "AGT Job" && hasAGTJob) ||
            (category.title === "AGT Fit" && hasAGTFit) ||
            (category.title === "AGT Health" && hasAGTHealth) ||
            (category.title === "AGT Legal" && hasAGTLegal) ||
            (category.title === "AGT Translate" && hasAGTTranslate);
          
          // Determinar si el servicio no está disponible según el orden especificado
          const isNotAvailable = !desiredOrder.includes(category.title);
        
          // Función para manejar la compra según el producto
          const handleBuy = () => {
            if (category.title === "AGT Full") {
              // Redirigir al enlace de Stripe para AGT Full
              window.location.href = "https://buy.stripe.com/bJedR89hAgi73lr8zN8EM00";
            } else if (category.title === "AGT Pro") {
              handleProCheckout();
            } else if (category.title === "AGT Edu") {
              onOpenAGTEdu();
            } else if (category.title === "AGT Ups") {
              onOpenAGTUps();
            } else if (category.title === "AGT Health") {
              onOpenAGTHealth();
            } else if (category.title === "AGT Job") {
              onOpenAGTJob();
            } else if (category.title === "AGT Fit") {
              // En lugar de llamar a onOpenAGTFit, llamamos a la nueva función
              handleAGTFitCheckout();
            } else if (category.title === "AGT Legal") {
              onOpenAGTLegal();
            } else if (category.title === "AGT Translate") {
              onOpenAGTTranslate();
            }
          };
          
          // Configurar los estilos de la imagen según la categoría
          let imageStyles = {};
          
          if (category.title === "AGT Full") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '50% center'
            };
          } else if (category.title === "AGT Ups") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '68% center'
            };
          } else if (category.title === "AGT Pro") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '56% center'
            };
          } else if (category.title === "AGT Edu") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '56% center'
            };
          } else if (category.title === "AGT Health") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '75% 70%', // Mostrar más abajo de la imagen (70% vertical)
              transform: 'translateY(40px)', // Mover la imagen hacia abajo
              height: 'calc(100% + 40px)', // Compensar el movimiento para que cubra todo
              marginTop: '-40px', // Ajustar el margen para mantener la posición correcta
            };
          } else if (category.title === "AGT Job") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '55% center'
            };
          } else if (category.title === "AGT Legal") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '54% center'
            };
          } else if (category.title === "AGT Translate") {
            imageStyles = { 
              backgroundSize: 'cover', 
              backgroundPosition: '54% center'
            };
          }

          // Traducir el título del producto
          const productKey = category.title.replace("AGT ", "").toLowerCase();
          const translatedTitle = category.title;
          const translatedPrice = t(`agtStore.products.agt${productKey.charAt(0).toUpperCase() + productKey.slice(1)}.price`, category.price);
        
          return (
            <CategoryCard key={category.id}>
              {/* Imagen de fondo que ocupa toda la tarjeta */}
              <CategoryImage
                image={category.image}
                title={translatedTitle}
                sx={imageStyles}
              />
            
              {/* Overlay con título y texto de AGT Ultra (excepto para AGT Full) */}
              <CategoryOverlay>
                {category.title === "AGT Health" ? (
                  <CategoryTitle>
                    {translatedTitle}<HealthPlusSign>+</HealthPlusSign>
                  </CategoryTitle>
                ) : (
                  <CategoryTitle>{translatedTitle}</CategoryTitle>
                )}
                
                {category.title === "AGT Health" ? (
                  <>
                    <HealthText>{t('agtStore.mainCategories.healthTagline', 'Family care matters')}</HealthText>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                ) : category.title === "AGT Job" ? (
                  <>
                    <JobText>{t('agtStore.mainCategories.jobTagline', 'You\'re hired!')}</JobText>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                ) : category.title === "AGT Fit" ? (
                  <>
                    <Typography sx={{ 
                      background: 'linear-gradient(90deg, #FF8C00 0%, #FF5722 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      fontWeight: 600,
                      fontSize: isMobile ? '15px' : '17px',
                      marginTop: '2px',
                      marginBottom: '2px',
                      display: 'inline-block'
                    }}>
                      {t('agtStore.mainCategories.fitTagline', 'Everything is possible')}
                    </Typography>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                ) : category.title === "AGT Legal" ? (
                  <>
                    <LegalText>{t('agtStore.mainCategories.legalTagline', 'Expertise at your service')}</LegalText>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                ) : category.title === "AGT Translate" ? (
                  <>
                    <TranslateText>{t('agtStore.mainCategories.translateTagline', 'Any language, anywhere')}</TranslateText>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                ) : category.title !== "AGT Full" && (
                  <>
                    <UltraIncludedText>{t('agtStore.mainCategories.ultraIncluded', 'AGT Ultra Included')}</UltraIncludedText>
                    <CategoryPrice>{translatedPrice}</CategoryPrice>
                  </>
                )}
              </CategoryOverlay>
            
              {/* Para AGT Full, mostrar el precio flotante justo encima del contenedor de botones */}
              {category.title === "AGT Full" && (
                <FloatingPrice>
                  <Typography sx={{ 
                    fontWeight: 600, 
                    fontSize: isMobile ? '14px' : '15px', 
                    marginBottom: '2px',
                    color: '#000000'
                  }}>
                    {t('agtStore.mainCategories.accessToAll', 'Access to all AGT\'s')}
                  </Typography>
                  {translatedPrice}
                </FloatingPrice>
              )}
              
              {/* Contenido con efecto de blur en la parte inferior - sin el precio dentro */}
              <CategoryContent>
                <CategoryButtonsContainer>
                  <LearnMoreButton 
                    onClick={() => scrollToSection(category.id)}
                    endIcon={<ChevronDown size={isMobile ? 14 : 16} />}
                  >
                    {t('agtStore.mainCategories.learnMore', 'Learn More')}
                  </LearnMoreButton>
                  
                  {isOwned ? (
                    <OwnedButton startIcon={<Check size={isMobile ? 14 : 16} />}>
                      {t('agtStore.mainCategories.owned', 'Owned')}
                    </OwnedButton>
                  ) : isNotAvailable ? (
                    <NotAvailableButton>
                      {t('agtStore.mainCategories.notAvailable', 'Not Available')}
                    </NotAvailableButton>
                  ) : category.title === "AGT Health" ? (
                    <AccessButton 
                      onClick={handleBuy}
                      endIcon={<ArrowRight size={isMobile ? 14 : 16} />}
                    >
                      {t('agtStore.mainCategories.access', 'Access')}
                    </AccessButton>
                  ) : (
                    <BuyButton 
                      onClick={handleBuy}
                      disabled={category.title === "AGT Fit" && loadingFit}
                    >
                      {category.title === "AGT Fit" && loadingFit ? (
                        // Usar AppleSpinner en lugar de CircularProgress
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16px', width: '16px' }}>
                          <AppleSpinner size="small" />
                        </Box>
                      ) : (
                        t('agtStore.mainCategories.buy', 'Buy')
                      )}
                    </BuyButton>
                  )}
                </CategoryButtonsContainer>
              </CategoryContent>
            </CategoryCard>
          );
        })}
      </CategoryCardsContainer>
      
      {/* Botones de navegación para el carrusel */}
      <NavigationButtons>
        <NavButton onClick={() => handleCategoryScroll(-1)}>
          <ChevronLeft size={isMobile ? 14 : 16} />
        </NavButton>
        <NavButton onClick={() => handleCategoryScroll(1)}>
          <ChevronRight size={isMobile ? 14 : 16} />
        </NavButton>
      </NavigationButtons>
    </MainCategoriesContainer>
  );
};

export default AGTMainCategoriesSection;